'use client';

import { useEffect, useMemo, useState } from 'react';
import { Breadcrumbs, Box, Typography } from '@mui/material';
import { usePathname, useSearchParams } from 'next/navigation';
import useSWR from 'swr';
import { AppLink } from '@/components/mui/app-link';
import { PortfolioSwitcher } from '@/app/(user)/carteira/_components/PortfolioSwitcher';
import { PortfolioCategorySwitcher } from '@/app/(user)/carteira/_components/PortfolioCategorySwitcher';
import { carteiraPath, labelForCarteiraTab, type CarteiraPrimaryTab } from '@/libs/carteira-route';
import {
  ATIVO_PORTFOLIO_SUBVIEWS,
  ativoPath,
  ATIVO_PRIMARY_TABS,
  labelForAtivoPortfolioSubview,
  labelForAtivoTab,
  type AtivoPrimaryTab,
} from '@/libs/ativo-route';

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export const REGISTERED_PARENT_PATHS: Record<string, true> = {
  '/': true,
  '/carteira': true,
  '/catalogo': true,
  '/blog': true,
  '/cursos': true,
  '/ferramentas': true,
  '/renda-fixa': true,
  '/renda-fixa/comparar': true,
  '/renda-fixa/simulador': true,
  '/comparador': true,
  '/mercado': true,
  '/precos': true,
  '/painel': true,
  '/funcionalidades': true,
  '/copiloto': true,
  '/relatorios-ia': true,
  '/referencia-rapida': true,
  '/ideias-para-investir-por-objetivo': true,
  '/rankings-de-fiis': true,
  '/contato': true,
  '/sobre': true,
  '/privacidade': true,
  '/termos': true,
  '/configuracoes': true,
  '/minha-conta': true,
  '/faturamento': true,
  '/emails': true,
  '/integracao-b3': true,
  '/admin': true,
};

export type ResolveBreadcrumbItemsOptions = {
  pathname: string;
  selectedCategory?: string;
  fundTypeSymbol?: string | null;
  activePortfolioName?: string | null;
};

export const resolveBreadcrumbItems = ({
  pathname,
  selectedCategory,
  fundTypeSymbol,
  activePortfolioName,
}: ResolveBreadcrumbItemsOptions): BreadcrumbItem[] => {
  const base: BreadcrumbItem[] = [{ label: 'Home', href: '/' }];
  const normalizedPath = pathname.trim() || '/';
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length === 0) return base;

  if (segments[0] === 'ativo') {
    const ticker = segments[1]?.toUpperCase() ?? '';
    const catalogoCrumbs: BreadcrumbItem[] = [{ label: 'Catálogo', href: '/catalogo' }];
    if (fundTypeSymbol) {
      catalogoCrumbs.push({
        label: fundTypeSymbol,
        href: `/catalogo/${fundTypeSymbol}`,
      });
    }
    if (ticker) {
      catalogoCrumbs.push({
        label: ticker,
        href: segments.length > 2 ? ativoPath.tab(ticker, 'resumo') : undefined,
      });
    }
    if (segments[2] && isAtivoPrimaryTabForBreadcrumb(segments[2])) {
      catalogoCrumbs.push({
        label: labelForAtivoTabForBreadcrumb(segments[2]),
        href: ativoPath.tab(ticker, segments[2] as never),
      });
      if (segments[3]) {
        catalogoCrumbs.push({
          label: labelForFundoSubviewForBreadcrumb(segments[2], segments[3]),
        });
      }
    }
    return [...base, ...catalogoCrumbs];
  }

  if (segments[0] === 'carteira') {
    const carteiraCrumbs: BreadcrumbItem[] = [{ label: 'Carteira', href: '/carteira' }];
    if (segments[1] === 'nova') {
      carteiraCrumbs.push({ label: 'Nova carteira' });
      return [...base, ...carteiraCrumbs];
    }
    if (segments[1] && segments[1] !== 'nova') {
      const activeTab =
        segments[2] && isCarteiraPrimaryTab(segments[2]) ? (segments[2] as CarteiraPrimaryTab) : 'resumo';
      carteiraCrumbs.push({
        label: labelForCarteiraTab(activeTab),
        href: carteiraPath.tab(segments[1], activeTab, selectedCategory),
      });
      if (segments[2] && isCarteiraPrimaryTab(segments[2]) && segments[3]) {
        carteiraCrumbs.push({
          label: mapSegmentLabel(segments[3]),
        });
      }
      if (activePortfolioName) {
        carteiraCrumbs.push({ label: activePortfolioName });
      }
    }
    return [...base, ...carteiraCrumbs];
  }

  if (segments[0] === 'cursos') {
    const courseCrumbs: BreadcrumbItem[] = [{ label: 'Cursos', href: '/cursos' }];
    if (segments[1] === 'trilhas') {
      courseCrumbs.push({ label: 'Trilhas' });
      if (segments[2]) {
        courseCrumbs.push({
          label: mapSegmentLabel(segments[2]),
          href: segments.length > 3 ? `/cursos/trilhas/${segments[2]}` : undefined,
        });
      }
      if (segments[3]) {
        courseCrumbs.push({
          label: mapSegmentLabel(segments[3]),
          href: segments.length > 5 ? `/cursos/trilhas/${segments[2]}/${segments[3]}` : undefined,
        });
      }
      if (segments[5]) courseCrumbs.push({ label: mapSegmentLabel(segments[5]) });
    } else if (segments[1]) {
      courseCrumbs.push({
        label: mapSegmentLabel(segments[1]),
        href: segments.length > 3 ? `/cursos/${segments[1]}` : undefined,
      });
      if (segments[3]) courseCrumbs.push({ label: mapSegmentLabel(segments[3]) });
    }
    return [...base, ...courseCrumbs];
  }

  let currentPath = '';
  const mapped = segments.map((segment, index) => {
    currentPath += `/${segment}`;
    const isLeaf = index === segments.length - 1;
    const hasRegisteredDestination = Boolean(REGISTERED_PARENT_PATHS[currentPath]);
    return {
      label: mapSegmentLabel(segment),
      href: isLeaf || !hasRegisteredDestination ? undefined : currentPath,
    };
  });
  return [...base, ...mapped];
};

const toTitle = (value: string) => {
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const mapSegmentLabel = (segment: string) => {
  const map: Record<string, string> = {
    painel: 'Painel',
    catalogo: 'Catálogo',
    precos: 'Planos',
    funcionalidades: 'Funcionalidades',
    ferramentas: 'Ferramentas',
    copiloto: 'Copiloto IA',
    'relatorios-ia': 'Relatórios IA',
    'relatorios-ai': 'Relatórios IA',
    curso: 'Curso',
    'referencia-rapida': 'Referência rápida',
    'renda-fixa': 'Tesouro',
    contato: 'Contato',
    sobre: 'Sobre',
    privacidade: 'Privacidade',
    termos: 'Termos',
    mercado: 'Mercado',
    comparador: 'Comparador de FIIs',
    'ideias-para-investir-por-objetivo': 'Ideias por objetivo',
    'rankings-de-fiis': 'Rankings de FIIs',
    cursos: 'Cursos',
    trilhas: 'Trilhas',
    carteira: 'Carteira',
    configuracoes: 'Configurações',
    'minha-conta': 'Minha conta',
    emails: 'E-mails',
    faturamento: 'Faturamento',
    'integracao-b3': 'Integração B3',
    admin: 'Admin',
    nova: 'Nova carteira',
    resumo: 'Resumo',
    ativos: 'Posições',
    dividendos: 'Dividendos',
    patrimonio: 'Patrimônio',
    movimentacoes: 'Movimentações',
    relatorios: 'Relatórios',
    importacoes: 'Importações',
    configuracao: 'Configuração',
    simulacao: 'Simulação',
    cotacao: 'Cotação',
    liquidez: 'Liquidez',
    'dados-cvm': 'Dados CVM',
    caixa: 'Caixa',
    recebiveis: 'Recebíveis',
    'titulos-fundos': 'Títulos e fundos',
    'a-pagar': 'A pagar',
    lancamentos: 'Lançamentos',
    diversificacao: 'Diversificação',
    'imoveis-composicao': 'Imóveis/Composição',
    'imoveis-risco': 'Imóveis/Risco',
    'imoveis-contratos': 'Imóveis/Contratos',
    'fundos-investidos': 'Fundos investidos',
    'ativos-financeiros': 'Ativos financeiros',
    'acoes-participacoes': 'Ações/Participações',
    'visao-geral': 'Visão geral',
  };
  return map[segment] ?? toTitle(segment);
};

const isCarteiraPrimaryTab = (value: string): value is CarteiraPrimaryTab =>
  [
    'resumo',
    'ativos',
    'dividendos',
    'patrimonio',
    'movimentacoes',
    'relatorios',
    'emails',
    'importacoes',
    'configuracao',
  ].includes(value);

const isAtivoPrimaryTabForBreadcrumb = (value: string): value is AtivoPrimaryTab =>
  (ATIVO_PRIMARY_TABS as readonly string[]).includes(value);

const labelForAtivoTabForBreadcrumb = (tab: string) =>
  isAtivoPrimaryTabForBreadcrumb(tab) ? labelForAtivoTab(tab) : toTitle(tab);

/**
 * Renders the canonical label for an ativo subview (carteira or caixa)
 * while keeping the carteira slugs in sync with the route helper.
 */
const labelForFundoSubviewForBreadcrumb = (tab: string, subview: string): string => {
  if (tab === 'carteira') {
    if ((ATIVO_PORTFOLIO_SUBVIEWS as readonly string[]).includes(subview)) {
      return labelForAtivoPortfolioSubview(subview as (typeof ATIVO_PORTFOLIO_SUBVIEWS)[number]);
    }
  }
  return mapSegmentLabel(subview);
};

export const AppBreadcrumbs = () => {
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const [fundTypeSymbol, setInstrumentTypeSymbol] = useState<string | null>(null);
  const selectedCategory = searchParams.get('categoria') ?? undefined;
  const segments = pathname.split('/').filter(Boolean);
  const isCarteiraResource = segments[0] === 'carteira' && segments[1] && segments[1] !== 'nova';
  const carteiraPortfolioId = isCarteiraResource ? segments[1] : null;
  const carteiraTab = isCarteiraResource && segments[2] ? segments[2] : 'resumo';
  const activeTab = isCarteiraPrimaryTab(carteiraTab) ? (carteiraTab as CarteiraPrimaryTab) : 'resumo';

  const { data: portfolioData, mutate: mutatePortfolios } = useSWR(
    isCarteiraResource ? '/api/user-portfolios' : null,
    async (url: string) => {
      const res = await fetch(url, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) {
        throw new Error('Failed to load portfolios');
      }
      return (await res.json()) as {
        portfolios: Array<{
          id: string;
          name: string;
          assetCategories: string[];
        }>;
        lastSelectedPortfolioId: string | null;
      };
    },
    {
      // The breadcrumb only reads portfolio names, which change via
      // server actions that revalidate the tree. Focus/visibility
      // refetches fired 5-6 requests per page for 0.2 kB each.
      revalidateOnFocus: false,
      dedupingInterval: 10_000,
    },
  );

  useEffect(() => {
    if (isCarteiraResource) {
      void mutatePortfolios();
    }
  }, [isCarteiraResource, carteiraPortfolioId, activeTab, mutatePortfolios]);

  useEffect(() => {
    if (!pathname.startsWith('/ativo/')) {
      setInstrumentTypeSymbol(null);
      return;
    }
    const ticker = pathname.split('/')[2] ?? '';
    if (!ticker) return;
    const controller = new AbortController();
    const loadInstrumentType = async () => {
      try {
        const res = await fetch(`/api/ticker?ticker=${encodeURIComponent(ticker)}`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = (await res.json()) as { fundTypeSymbol?: string | null };
        setInstrumentTypeSymbol(data.fundTypeSymbol ?? null);
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') {
          setInstrumentTypeSymbol(null);
        }
      }
    };
    loadInstrumentType();
    return () => controller.abort();
  }, [pathname]);

  const portfolioOptions = portfolioData?.portfolios ?? [];
  const activePortfolioId =
    carteiraPortfolioId && portfolioOptions.some((portfolio) => portfolio.id === carteiraPortfolioId)
      ? carteiraPortfolioId
      : portfolioData?.lastSelectedPortfolioId &&
          portfolioOptions.some((portfolio) => portfolio.id === portfolioData.lastSelectedPortfolioId)
        ? portfolioData.lastSelectedPortfolioId
        : (portfolioOptions[0]?.id ?? null);

  const activePortfolioName = portfolioOptions.find((portfolio) => portfolio.id === activePortfolioId)?.name ?? null;

  const crumbs = useMemo<BreadcrumbItem[]>(() => {
    return resolveBreadcrumbItems({
      pathname,
      selectedCategory,
      fundTypeSymbol,
      activePortfolioName,
    });
  }, [activePortfolioName, fundTypeSymbol, pathname, selectedCategory]);

  return (
    <Box sx={{ py: 0.25 }}>
      <Breadcrumbs
        aria-label='breadcrumbs'
        separator='›'
        sx={{
          fontSize: 12,
          color: 'inherit',
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none',
          },
          '& .MuiBreadcrumbs-ol::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {crumbs.map((crumb, index) =>
          crumb.href ? (
            <AppLink
              key={`${crumb.label}-${crumb.href}`}
              href={crumb.href}
              underline='hover'
              sx={{
                color: 'inherit',
                opacity: 0.82,
                whiteSpace: 'nowrap',
                fontSize: 12,
                fontWeight: 600,
                '&:hover': {
                  color: 'hsl(var(--primary))',
                  opacity: 1,
                },
              }}
            >
              {crumb.label}
            </AppLink>
          ) : (
            <Typography
              key={`${crumb.label}-${index}`}
              color='inherit'
              sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {crumb.label}
            </Typography>
          ),
        )}
        {isCarteiraResource && activePortfolioId && portfolioOptions.length > 0 ? (
          <Box
            key='carteira-switcher'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'inherit',
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 142, sm: 164 },
                maxWidth: { xs: 180, sm: 220 },
              }}
            >
              <PortfolioSwitcher
                activePortfolioId={activePortfolioId}
                activeTab={activeTab}
                newHref='/carteira/nova'
                portfolios={portfolioOptions}
                size='small'
                minWidth={142}
                sx={{
                  width: '100%',
                  minWidth: { xs: 142, sm: 164 },
                  maxWidth: { xs: 180, sm: 220 },
                }}
              />
            </Box>
            <Box
              sx={{
                borderLeft: '1px solid',
                borderColor: 'currentColor',
                pl: 1,
              }}
            >
              <PortfolioCategorySwitcher
                categories={
                  portfolioOptions.find((portfolio) => portfolio.id === activePortfolioId)?.assetCategories ?? []
                }
                value={selectedCategory}
              />
            </Box>
          </Box>
        ) : null}
      </Breadcrumbs>
    </Box>
  );
};
